import { z } from "zod";

import type {
  TextComposerChannelTransformValue,
  TextComposerDerivedOutput,
  TextComposerFallbackChannel,
  TextComposerPrimaryChannel,
} from "../channel/channelDerivation";
import type { TextComposerAuthoringState } from "../editor/authoringState";

import { textTransportMediumSchema } from "@/frames/schemas";

export const textComposerPreviewMediumSchema = textTransportMediumSchema;

export type TextComposerPreviewMedium = z.infer<
  typeof textComposerPreviewMediumSchema
>;

/**
 * Wire-format schema for the authoring state inside `content_json`.
 *
 * Only the core `bodyText` is validated here; everything else passes through
 * so extensions are free to add their own keys without core edits. Each
 * extension is responsible for validating its slice via `persistence.deserialize`.
 */
const textComposerWireAuthoringStateSchema = z
  .object({
    bodyText: z.string().optional(),
  })
  .passthrough();

const textComposerAuthoringStateWithChannelsSchema =
  textComposerWireAuthoringStateSchema.extend({
    primaryChannel: z.enum(["RCS", "SMS", "MMS", "INVALID"]),
    fallbackChannel: z.enum(["SMS", "MMS"]).nullable(),
    fallbackUnlinked: z.boolean().optional(),
  });

const textComposerRcsTransformedSchema = z
  .object({
    content: z
      .object({
        body: z.union([
          z
            .object({
              type: z.literal("TEXT"),
              text: z.string(),
            })
            .strict(),
          z
            .object({
              type: z.literal("IMAGE"),
              url: z.string(),
              text: z.string().optional(),
            })
            .strict(),
          z
            .object({
              type: z.literal("DOCUMENT"),
              url: z.string(),
              text: z.string().optional(),
            })
            .strict(),
        ]),
        buttons: z
          .array(
            z
              .object({
                type: z.literal("OPEN_URL"),
                text: z.string(),
                url: z.string(),
              })
              .strict(),
          )
          .optional(),
        suggestions: z
          .array(
            z.union([
              z
                .object({
                  type: z.literal("REPLY"),
                  text: z.string(),
                  postbackData: z.string(),
                })
                .strict(),
              z
                .object({
                  type: z.literal("OPEN_URL"),
                  text: z.string(),
                  postbackData: z.string(),
                  url: z.string(),
                  application: z.literal("BROWSER"),
                })
                .strict(),
              z
                .object({
                  type: z.literal("DIAL_PHONE"),
                  text: z.string(),
                  postbackData: z.string(),
                  phoneNumber: z.string(),
                })
                .strict(),
            ]),
          )
          .optional(),
      })
      .passthrough(),
  })
  .strict();

const textComposerTransformedOutputSchema =
  textComposerRcsTransformedSchema.nullable();

export const textComposerFrameVariantContentJsonSchema = z
  .object({
    authoringState: textComposerAuthoringStateWithChannelsSchema,
    primaryChannel: z.enum(["RCS", "SMS", "MMS", "INVALID"]),
    fallbackChannel: z.enum(["SMS", "MMS"]).nullable(),
    transformed: textComposerTransformedOutputSchema,
  })
  .strict();

export const frameVariantOutputSchema = z
  .object({
    medium: textComposerPreviewMediumSchema,
    content_json: textComposerFrameVariantContentJsonSchema,
  })
  .strict();

export const frameOutputSchema = z
  .object({
    frame_variants: z.array(frameVariantOutputSchema),
  })
  .strict();

export const framePreviewOutputSchema = z
  .object({
    frame: frameOutputSchema,
  })
  .strict();

export type TextComposerFrameVariantOutput = z.infer<
  typeof frameVariantOutputSchema
>;
/** `content_json` for SMS/RCS/MMS variants built from Text Composer state. */
export type TextComposerFrameVariantContentJson =
  TextComposerFrameVariantOutput["content_json"];
export type TextComposerFrameOutput = z.infer<typeof frameOutputSchema>;
export type TextComposerFramePreviewOutput = z.infer<
  typeof framePreviewOutputSchema
>;

export function mapChannelToPreviewMedium(
  channel: TextComposerPrimaryChannel | TextComposerFallbackChannel,
): TextComposerPreviewMedium | null {
  if (channel === "SMS") {
    return "sms";
  }

  if (channel === "RCS") {
    return "rcs";
  }

  if (channel === "MMS") {
    return "mms";
  }

  return null;
}

interface BuildFrameOutputArgs {
  authoringState: TextComposerAuthoringState;
  fallbackAuthoringState?: TextComposerAuthoringState;
  derivedOutput: TextComposerDerivedOutput;
  primaryTransform: TextComposerChannelTransformValue;
  fallbackTransform: TextComposerChannelTransformValue;
}

export function buildFrameOutput({
  authoringState,
  fallbackAuthoringState,
  derivedOutput,
  primaryTransform,
  fallbackTransform,
}: BuildFrameOutputArgs): TextComposerFramePreviewOutput {
  const authoringStateWithChannels = {
    ...authoringState,
    primaryChannel: derivedOutput.primaryChannel,
    fallbackChannel: derivedOutput.fallbackChannel,
  };
  const fallbackAuthoringStateWithChannels =
    fallbackAuthoringState === undefined
      ? authoringStateWithChannels
      : {
          ...fallbackAuthoringState,
          primaryChannel: derivedOutput.primaryChannel,
          fallbackChannel: derivedOutput.fallbackChannel,
          fallbackUnlinked: true,
        };
  const variants: TextComposerFrameVariantOutput[] = [];
  const primaryMedium = mapChannelToPreviewMedium(derivedOutput.primaryChannel);
  const fallbackMedium = mapChannelToPreviewMedium(
    derivedOutput.fallbackChannel,
  );

  if (primaryMedium) {
    variants.push({
      medium: primaryMedium,
      content_json: {
        authoringState: authoringStateWithChannels,
        primaryChannel: derivedOutput.primaryChannel,
        fallbackChannel: derivedOutput.fallbackChannel,
        transformed: primaryTransform,
      },
    });
  }

  if (fallbackMedium) {
    variants.push({
      medium: fallbackMedium,
      content_json: {
        authoringState: fallbackAuthoringStateWithChannels,
        primaryChannel: derivedOutput.primaryChannel,
        fallbackChannel: derivedOutput.fallbackChannel,
        transformed: fallbackTransform,
      },
    });
  }

  return framePreviewOutputSchema.parse({
    frame: {
      frame_variants: variants,
    },
  });
}

/**
 * Resolves the frame variant (medium + content_json) for preview/test-send
 * matching the given RCS/SMS/MMS channel.
 */
export function resolveFrameVariantForChannel(
  framePreview: TextComposerFramePreviewOutput,
  channel: TextComposerPrimaryChannel | TextComposerFallbackChannel,
): Pick<TextComposerFrameVariantOutput, "medium" | "content_json"> | null {
  const medium = mapChannelToPreviewMedium(channel);
  if (!medium) {
    return null;
  }
  const variant = framePreview.frame.frame_variants.find(
    (row) => row.medium === medium,
  );
  if (!variant) {
    return null;
  }
  return { medium: variant.medium, content_json: variant.content_json };
}
